// Kundensicht (Ops-Impersonation, Janik 20.08.2026): `?impersonate=<uid>` merkt
// sich die Ziel-UID in sessionStorage; ab dann geht sie als `X-Cortex-User` an
// alle same-origin /api-Aufrufe. Autorisierung passiert AUSSCHLIESSLICH im
// Server (requireAuth): nur wenn die echte Cookie-Session ein Admin ist, wird
// der Header beachtet — für alle anderen ist er wirkungslos. Da auch
// GET /api/auth/me durch requireAuth läuft, bootet die App vollständig als
// Ziel-Nutzer (inkl. autopilotOnly-Gates aus LEA-531).
//
// WICHTIG: Klicks in Kundensicht wirken ECHT als Kunde (gleiche Routen, echte
// Writes) — deshalb das unübersehbare Banner. Freigaben gehören dem Kunden und
// werden nie aus der Kundensicht erteilt. Beenden: `?impersonate=off`.
//
// Dieses Modul wird als ALLERERSTER Import in main.tsx geladen, damit der
// fetch-Wrapper vor dem ersten API-Call steht. Es darf den Boot nie brechen —
// alles defensiv in try/catch.

const KEY = 'cortexKundensichtUid';

(() => {
  try {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get('impersonate');
    if (qp !== null) {
      if (!qp || qp === 'off') sessionStorage.removeItem(KEY);
      else if (/^[A-Za-z0-9]{6,64}$/.test(qp)) sessionStorage.setItem(KEY, qp);
      url.searchParams.delete('impersonate');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    /* Kundensicht darf den Boot nie brechen */
  }
})();

export const kundensichtUid = (): string | null => {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};

// Globaler fetch-Wrapper statt Umbau aller Aufrufstellen: die App nutzt
// verstreute `fetch('/api/…', {credentials:'include'})`-Calls ohne zentralen
// Client. Der Wrapper fasst NUR same-origin /api-Requests an.
const origFetch = window.fetch.bind(window);
window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
  try {
    const uid = kundensichtUid();
    if (uid) {
      const u =
        typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url || '';
      if (u.startsWith('/api') || u.startsWith(`${window.location.origin}/api`)) {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? (input as Request).headers : undefined));
        headers.set('X-Cortex-User', uid);
        init = { ...init, headers };
      }
    }
  } catch {
    /* im Zweifel unverändert durchreichen */
  }
  return origFetch(input as RequestInfo, init);
}) as typeof window.fetch;

// Unübersehbares Banner, solange die Kundensicht aktiv ist. Bewusst ohne
// React/innerHTML (UID stammt aus der URL — nur als textContent einsetzen).
function zeigeBanner() {
  try {
    const uid = kundensichtUid();
    if (!uid || document.getElementById('kundensicht-banner')) return;
    const el = document.createElement('div');
    el.id = 'kundensicht-banner';
    el.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;z-index:2147483000;background:#B45309;color:#fff;' +
      'font:600 13px/1.4 -apple-system,BlinkMacSystemFont,sans-serif;padding:8px 16px;' +
      'display:flex;gap:14px;align-items:center;justify-content:center;box-shadow:0 -2px 8px rgba(0,0,0,.25)';
    const text = document.createElement('span');
    text.textContent = `Kundensicht aktiv (${uid.slice(0, 8)}…) — Klicks wirken als KUNDE. Keine Freigaben von hier erteilen.`;
    const ende = document.createElement('a');
    ende.href = '?impersonate=off';
    ende.textContent = 'Beenden';
    ende.style.cssText = 'color:#fff;text-decoration:underline;font-weight:700';
    el.append(text, ende);
    document.body.appendChild(el);
  } catch {
    /* Anzeige-Komfort, nie Boot-kritisch */
  }
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', zeigeBanner);
} else {
  zeigeBanner();
}
